/**
 * Registrierung — das Stueck, das bis zum 24.09.2026 fehlte.
 *
 * Bis dahin konnte sich niemand selbst ein Konto anlegen: die Zustimmungsseite
 * des Connectors kennt nur Anmelden, und eine Registrierungsseite gab es
 * nirgends. Ein Testkunde brauchte deshalb entweder einen API-Key oder jemanden,
 * der ihm von Hand einen Auth-Nutzer anlegt.
 *
 * NICHT VERWECHSELN: hier entsteht der Auth-Nutzer. Die Zeile in
 * `public.accounts` legt `oauth.ts` beim ersten Werkzeugaufruf selbst an.
 *
 * WARUM DIE BESTAETIGUNG NICHT UEBERSPRUNGEN WIRD: `SIM_SIGNUP_CREDITS` wird
 * erst nach bestaetigter Adresse gutgeschrieben, bis dahin meldet `get_balance`
 * ein `signup_credits_pending`. Die Bestaetigung ist also schon heute tragend
 * und keine Formalie.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Feld, Haken, Hinweis, Knopf, Rahmen, RegistrierRahmen, Verweis } from '../ui';
import { fehlertext, supabase } from '../supabase';
import { MAIL_ABSENDER, PFAD_BESTAETIGT, redirectTo } from '../config';
import { einmal, melde } from '../messung';
import { STANDARDZIEL } from '../ziel';
import { webmailFuer } from '../postfach';
import { mailSprache, t } from '../sprache';
import { useTurnstile } from '../turnstile';
import { EINWILLIGUNG_TEXT } from './NewsletterZeile';
import { AGB_URL } from './Bestellung';

const DATENSCHUTZ_URL = 'https://www.99takes.com/de/datenschutz';
/** Englische Übersetzung zur Information (99takes-website, #275). */
const TERMS_EN_URL = 'https://www.99takes.com/terms';
const PRIVACY_EN_URL = 'https://www.99takes.com/privacy';

/** Supabase lehnt kuerzere ab; das hier sagt es vorher, statt nach dem Absenden. */
const MIN_ZEICHEN = 8;

/**
 * Sekunden bis „Mail erneut senden" wieder geht. Supabase drosselt Auth-Mails
 * je Adresse; wer schneller klickt, bekommt eine Fehlermeldung statt einer
 * Mail und haelt das fuer kaputt.
 */
const SPERRE_SEKUNDEN = 60;

export default function Registrieren() {
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [verschickt, setVerschickt] = useState(false);
  // Newsletter (newsletter.ts): leer vorbelegt. Die Bestätigungsmail kommt nach der Bestätigung des Kontos.
  const [newsletter, setNewsletter] = useState(false);
  const pruefung = useTurnstile('signup');

  function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler('');
    if (passwort.length < MIN_ZEICHEN) {
      setFehler(t(`Das Passwort braucht mindestens ${MIN_ZEICHEN} Zeichen.`, `The password needs at least ${MIN_ZEICHEN} characters.`));
      return;
    }
    pruefung.ausfuehren(registrieren);
  }

  async function registrieren(captchaToken: string) {
    setLaedt(true);
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password: passwort,
      // `sprache` wählt die Sprache der Mails, auch der Bestätigung (sprache.ts).
      options: { emailRedirectTo: redirectTo(PFAD_BESTAETIGT), data: { sprache: mailSprache(), ...(newsletter ? { newsletter: true } : {}) }, captchaToken },
    });
    setLaedt(false);

    if (error) {
      setFehler(fehlertext(error));
      return;
    }
    // Ist die Adresse schon vergeben, antwortet Supabase bewusst wie bei einem
    // Erfolg, nur ohne neue Identitaet — sonst waere das Formular ein Verzeichnis
    // aller Kunden. Wir zeigen darum dieselbe Meldung; wer das Konto hat, bekommt
    // eine Mail, die ihn ans Anmelden erinnert.
    void data;
    // Auch bei schon vergebener Adresse: die Antwort ist absichtlich dieselbe.
    // Die abgeschlossene Registrierung misst erst sign_up nach der Bestaetigung.
    melde({ event: 'register_submit' });
    setVerschickt(true);
  }

  if (verschickt) {
    return <CodeEingabe email={email.trim()} andereAdresse={() => { setVerschickt(false); setPasswort(''); }} />;
  }

  return (
    <RegistrierRahmen
      titel={t('Konto anlegen', 'Create account')}
      unter={<>{t('Danach verbindest du 99takes mit Claude.', 'Then you connect 99takes to Claude.')}<span className="lg:hidden"> <a href="#was-ist-99takes" className="text-marke-tief underline decoration-marke/40 underline-offset-[3px] hover:decoration-marke">{t('Was ist 99takes?', 'What is 99takes?')}</a></span></>}
      anmelden={<>{t('Schon ein Konto?', 'Already have an account?')} <Verweis to="/login">{t('Anmelden', 'Sign in')}</Verweis></>}
    >
      <form onSubmit={absenden}>
        {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
        <Feld
          label={t('E-Mail', 'Email')} type="email" name="email" autoComplete="email" required autoFocus
          value={email} onChange={e => setEmail(e.target.value)} disabled={laedt}
        />
        <Feld
          label={t('Passwort', 'Password')} type="password" name="new-password" autoComplete="new-password" required
          hinweis={passwort && passwort.length < MIN_ZEICHEN
            ? t(`Noch ${MIN_ZEICHEN - passwort.length} Zeichen.`, MIN_ZEICHEN - passwort.length === 1 ? '1 more character.' : `${MIN_ZEICHEN - passwort.length} more characters.`)
            : t(`Mindestens ${MIN_ZEICHEN} Zeichen.`, `At least ${MIN_ZEICHEN} characters.`)}
          value={passwort} onChange={e => setPasswort(e.target.value)} disabled={laedt}
        />
        <label className="mb-4 flex items-start gap-2.5 text-[0.8125rem] leading-relaxed text-text-leise">
          <input type="checkbox" name="newsletter" checked={newsletter} onChange={e => setNewsletter(e.target.checked)} disabled={laedt} className="mt-[0.2rem] shrink-0" />
          <span>{EINWILLIGUNG_TEXT()}</span>
        </label>
        {pruefung.feld}
        <Knopf type="submit" laedt={laedt || pruefung.wartet}>{t('Konto anlegen', 'Create account')}</Knopf>
        {/* Was man bekommt, direkt unter dem Knopf statt darüber (Entwurf
            „99takes Signup Politur", 12.10.2026: darüber schoben drei Zeilen
            den Knopf bei 340 x 640 unter den Rand). Nur, was stimmt: kein
            Zahlungsmittel, und das Startguthaben wird nach der Bestätigung
            gebucht (SIM_SIGNUP_CREDITS). Die Zahl steht bewusst nicht hier,
            sie ist ein Function-Secret und würde sonst still veralten. */}
        <p className="mt-3 mb-0 flex flex-wrap gap-x-3.5 gap-y-1 text-[0.8125rem] text-text-leise">
          {[t('Startguthaben nach Bestätigung', 'Starting credits after confirmation'), t('Keine Zahlungsdaten', 'No payment details')].map(v => (
            <span key={v} className="inline-flex items-center gap-1.5"><span className="text-gut"><Haken /></span>{v}</span>
          ))}
        </p>
        {/* Mails zum Guthaben (#155) sind eher Werbung als Transaktionsmail.
            Der Hinweis bei der Erhebung der Adresse gehört dazu (§ 7 Abs. 3
            UWG); jede Mail trägt den Abmeldelink. */}
        <p className="mt-4 mb-0 border-t border-linie-zart pt-3.5 text-[0.78125rem] leading-relaxed text-text-hell">
          {t(
            <>Wir schreiben dir, wenn dein Guthaben knapp wird, und sagen dir, wie du mehr Credits bekommst.
          Abmelden kannst du dich jederzeit über den Link in jeder dieser Mails.</>,
            <>We write to you when your balance runs low and tell you how to get more credits.
          You can unsubscribe at any time with the link in each of these emails.</>,
          )}
        </p>
        {/* AGB auch ohne Kauf (#275): das Startguthaben ist schon eine
            Leistung nach den AGB. Verbindlich ist die deutsche Fassung
            (AGB § 16 Abs. 3), die englische ist eine Übersetzung zur
            Information. */}
        <p className="mt-2 mb-0 text-[0.78125rem] leading-relaxed text-text-hell">
          {t(
            <>Es gelten unsere <a href={AGB_URL} target="_blank" rel="noopener" className="text-marke-tief underline">AGB</a>.
          Wie wir mit deinen Daten umgehen, steht in der <a href={DATENSCHUTZ_URL} target="_blank" rel="noopener" className="text-marke-tief underline">Datenschutzerklärung</a>.</>,
            <>Our <a href={TERMS_EN_URL} target="_blank" rel="noopener" className="text-marke-tief underline">terms</a> apply (binding in German, English for information).
          How we handle your data is described in our <a href={PRIVACY_EN_URL} target="_blank" rel="noopener" className="text-marke-tief underline">privacy policy</a>.</>,
          )}
        </p>
      </form>
    </RegistrierRahmen>
  );
}

/**
 * Stellen des Bestätigungscodes. Muss zu „Email OTP Length“ in Supabase passen
 * (*Authentication → Sign In / Providers → Email*), sonst schickt das Feld zu
 * früh oder nie ab. Nachgesehen am 05.11.2026 in Produktion: 8 Stellen, gültig
 * 900 s. Wer den Wert dort ändert, ändert ihn hier mit.
 */
export const CODE_LAENGE = 8;

/**
 * Nach dem Absenden (seit 05.11.2026): den Code aus der Mail eingeben, statt
 * auf einen Link zu klicken (Entwurf A „Ein Feld“ in „Code statt Link“,
 * Claude-Design-Projekt 7f590d08-…, gewählt von Robert; Vorbild Higgsfield).
 *
 * WARUM: Vorher stand hier „Sieh in dein Postfach“, und der Weg ging über den
 * Link in einen neuen Tab und von dort weiter. Supabase lässt E-Mail und
 * Passwort ohne Bestätigung nicht hinein (supabase/auth#2513), also bleibt
 * die Bestätigung, aber auf derselben Seite: Code einfügen, verifyOtp liefert
 * die Sitzung, und NurAbgemeldet schickt sofort weiter in den Einstieg.
 * Startguthaben, Konto-Übernahme und Newsletter hängen weiter an der
 * Bestätigung in Supabase und bleiben, wie sie sind.
 *
 * Der Code steht im Betreff und groß in der Mail (supabase/templates,
 * `confirmation`); der Link darunter führt wie bisher über /confirmed.
 * Auch die Anmeldung zeigt dieses Feld, wenn die Adresse noch offen ist.
 */
export function CodeEingabe({ email, andereAdresse, sofortSenden = false, ziel = STANDARDZIEL }: {
  email: string;
  /** Wohin nach dem Code (aus der Anmeldung: deren `next`). */
  ziel?: string;
  andereAdresse?: () => void;
  /** Aus der Anmeldung: die erste Mail kann alt sein, „Neuen Code senden“ geht sofort. */
  sofortSenden?: boolean;
}) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [prueft, setPrueft] = useState(false);
  const [fehler, setFehler] = useState('');
  const [sperre, setSperre] = useState(sofortSenden ? 0 : SPERRE_SEKUNDEN);
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState('');
  const feld = useRef<HTMLInputElement>(null);
  const pruefung = useTurnstile('code');

  useEffect(() => {
    if (sperre <= 0) return;
    const z = setTimeout(() => setSperre(s => s - 1), 1000);
    return () => clearTimeout(z);
  }, [sperre]);

  // Ein Widget für beides: Prüfen und Neusenden. Ist das CAPTCHA in Supabase
  // an, braucht auch verifyOtp einen Token (turnstile.test.ts); der Hook setzt
  // das Widget nach jedem Aufruf zurück, also hat jeder Aufruf seinen eigenen.
  function pruefen(wert: string) {
    if (prueft || wert.length < CODE_LAENGE) return;
    setFehler('');
    pruefung.ausfuehren(token => verifizieren(wert, token));
  }

  async function verifizieren(wert: string, captchaToken: string) {
    setPrueft(true);
    const { data, error } = await supabase.auth.verifyOtp({ email, token: wert, type: 'signup', options: { captchaToken } });
    if (error || !data.session) {
      setPrueft(false);
      const m = (error?.message ?? '').toLowerCase();
      setFehler(m.includes('rate limit') || m.includes('too many') || m.includes('failed to fetch')
        ? fehlertext(error)
        : t('Der Code stimmt nicht oder ist abgelaufen. Nimm den aus der neuesten Mail oder lass dir einen neuen schicken.',
          'The code is wrong or has expired. Use the one from the latest email or have a new one sent.'));
      feld.current?.select();
      return;
    }
    // Die Registrierung ist hier abgeschlossen: Adresse bestätigt, Sitzung da.
    einmal(`sign_up:${data.session.user.id}`, { event: 'sign_up', method: 'email', weg: 'code' });
    navigate(ziel, { replace: true });
  }

  function eingabe(roh: string) {
    // Nur Ziffern: eingefügt wird oft „482 913“ oder mit Zeilenumbruch.
    const wert = roh.replace(/\D/g, '').slice(0, CODE_LAENGE);
    setCode(wert);
    if (fehler) setFehler('');
    if (wert.length === CODE_LAENGE) pruefen(wert);
  }

  async function nochmal(captchaToken: string) {
    setSendet(true);
    setMeldung('');
    setFehler('');
    const { error } = await supabase.auth.resend({ type: 'signup', email, options: { emailRedirectTo: redirectTo(PFAD_BESTAETIGT), captchaToken } });
    setSendet(false);
    if (error) { setFehler(fehlertext(error)); return; }
    setMeldung(t('Neuer Code ist unterwegs. Der alte gilt nicht mehr.', 'A new code is on its way. The old one no longer works.'));
    setCode('');
    setSperre(SPERRE_SEKUNDEN);
    feld.current?.focus();
  }

  return (
    <Rahmen
      schritt={2}
      titel={t('Gib den Code aus der Mail ein', 'Enter the code from the email')}
      unter={t(
        <>Wir haben ihn an <strong className="text-tinte [overflow-wrap:anywhere]">{email}</strong> geschickt.</>,
        <>We sent it to <strong className="text-tinte [overflow-wrap:anywhere]">{email}</strong>.</>,
      )}
    >
      <form onSubmit={e => { e.preventDefault(); pruefen(code); }}>
        <label htmlFor="code" className="sr-only">{t('Code', 'Code')}</label>
        <input
          ref={feld} id="code" name="code" autoFocus
          inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" spellCheck={false}
          placeholder={'_'.repeat(CODE_LAENGE)}
          value={code} onChange={e => eingabe(e.target.value)} disabled={prueft}
          aria-invalid={!!fehler} aria-describedby={fehler ? 'code-fehler' : undefined}
          className={`box-border h-14 w-full rounded-xl border-[1.5px] bg-flaeche px-[1.125rem] text-[1.625rem] font-semibold tracking-[0.32em] text-tinte tabular-nums outline-none
                      placeholder:font-medium placeholder:text-linie-stark disabled:opacity-60
                      ${fehler ? 'border-schlecht shadow-[0_0_0_3px_rgb(164_51_42/0.12)]' : 'border-marke shadow-[0_0_0_3px_rgb(63_138_135/0.16)]'}`}
        />
        {fehler && <p id="code-fehler" role="alert" className="mt-3 mb-0 text-[0.84375rem] leading-relaxed text-schlecht">{fehler}</p>}
        {(prueft || pruefung.wartet) && <p role="status" className="mt-3 mb-0 text-[0.84375rem] text-text-leise">{t('Wird geprüft …', 'Checking …')}</p>}
        {meldung && !fehler && <p role="status" className="mt-3 mb-0 text-[0.84375rem] text-gut">{meldung}</p>}
        {/* Abschicken geht von selbst bei voller Länge; der Knopf ist für
            Tastatur und Screenreader da und für Codes, die anders lang sind. */}
        <button type="submit" className="sr-only">{t('Bestätigen', 'Confirm')}</button>
      </form>

      <div className="mt-4 flex flex-col items-center gap-3">
        {pruefung.feld}
        <button
          type="button" onClick={() => pruefung.ausfuehren(nochmal)} disabled={sendet || pruefung.wartet || sperre > 0}
          className="inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-1 text-[0.875rem] text-marke-tief disabled:cursor-default disabled:text-text-hell"
        >
          <span aria-hidden>↻</span>
          {sendet || pruefung.wartet ? t('Wird verschickt …', 'Sending …')
            : sperre > 0 ? <>{t('Neuen Code senden in', 'Send a new code in')} <span className="tabular-nums">{sperre} s</span></>
            : t('Neuen Code senden', 'Send a new code')}
        </button>
        {andereAdresse && (
          <p className="m-0 text-[0.8125rem] text-text-hell">
            {t('Falsche Adresse?', 'Wrong address?')}{' '}
            <button type="button" onClick={andereAdresse} className="cursor-pointer border-0 bg-transparent p-0 text-[0.8125rem] text-marke-tief underline decoration-marke/40 underline-offset-[3px] hover:decoration-marke">{t('Andere eingeben', 'Enter another')}</button>
          </p>
        )}
      </div>

      <p className="mt-5 mb-0 border-t border-linie-zart pt-4 text-[0.8125rem] leading-relaxed text-text-hell">
        {t(
          <>Danach geht es direkt weiter, und dein Startguthaben ist gebucht. Die Mail kommt von {MAIL_ABSENDER.name}, {MAIL_ABSENDER.adresse}; sieh auch im Spam nach.</>,
          <>Then you continue right away, and your starting credits are added. The email comes from {MAIL_ABSENDER.name}, {MAIL_ABSENDER.adresse}; check your spam folder too.</>,
        )}
      </p>
    </Rahmen>
  );
}

/**
 * Für einen abgelaufenen Link auf /confirmed (seit 05.11.2026 nur noch dort;
 * nach der Registrierung steht `CodeEingabe`). Vorher war der einzige Ausweg „versuch es noch einmal"
 * — also eine zweite Registrierung, die bei vergebener Adresse still nichts
 * tut. Jetzt: dieselbe Mail noch einmal, oder zurueck zur Adresse, falls sie
 * falsch war. Und der Absender steht da, damit man weiss, wonach man sucht.
 * Seit 12.10.2026 dazu ein Knopf ins Webmail, wo die Domain den Anbieter
 * verraet. Auch auf /confirmed, wenn ein abgelaufener Link neu verschickt
 * wurde; dort ohne „Andere Adresse eingeben".
 */
export function Postfach({ email, andereAdresse }: { email: string; andereAdresse?: () => void }) {
  const [sperre, setSperre] = useState(SPERRE_SEKUNDEN);
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<{ art: 'gut' | 'schlecht'; text: string } | null>(null);
  const pruefung = useTurnstile('resend');

  useEffect(() => {
    if (sperre <= 0) return;
    const t = setTimeout(() => setSperre(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [sperre]);

  async function nochmal(captchaToken: string) {
    setSendet(true);
    setMeldung(null);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: redirectTo(PFAD_BESTAETIGT), captchaToken },
    });
    setSendet(false);
    if (error) {
      setMeldung({ art: 'schlecht', text: fehlertext(error) });
      return;
    }
    setMeldung({ art: 'gut', text: t('Noch einmal verschickt.', 'Sent again.') });
    setSperre(SPERRE_SEKUNDEN);
  }

  const webmail = webmailFuer(email, MAIL_ABSENDER.adresse);

  return (
    <Rahmen
      schritt={2}
      titel={t('Sieh in dein Postfach', 'Check your inbox')}
      unter={t(
        <>Wir haben eine Bestätigung an <strong className="text-tinte [overflow-wrap:anywhere]">{email}</strong> geschickt. Der Link darin schaltet dein Konto frei.</>,
        <>We sent a confirmation to <strong className="text-tinte [overflow-wrap:anywhere]">{email}</strong>. The link in it activates your account.</>,
      )}
    >
      {/* Der Weg ins Postfach als Knopf, wo die Domain den Anbieter verrät
          (postfach.ts). Bei einer Firmenadresse fehlt er. */}
      {webmail && (
        <a
          href={webmail.url} target="_blank" rel="noopener noreferrer"
          className="flex h-[2.875rem] items-center justify-center gap-2 rounded-lg bg-knopf px-4 text-[0.9375rem] font-semibold text-auf-marke no-underline
                     shadow-[inset_0_1px_0_rgb(255_255_255/0.14),0_1px_2px_rgb(20_18_12/0.18)] transition-colors duration-150 hover:bg-knopf-hover"
        >
          {t(`${webmail.name} öffnen`, `Open ${webmail.name}`)}
          <svg aria-hidden viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="size-3.5"><path d="M6 3h7v7" /><path d="M13 3L4 12" /></svg>
          <span className="sr-only">{t('(neuer Tab)', '(new tab)')}</span>
        </a>
      )}
      {/* Absender in einer Zeile; die Teile brechen nur zwischeneinander um,
          nie mitten in der Adresse (vorher „<no-“ und „reply@…“). */}
      <p className={`m-0 flex flex-wrap items-baseline gap-x-2 text-[0.8125rem] text-text-hell ${webmail ? 'mt-2.5' : ''}`}>
        <span>{t('Absender', 'Sender')}</span>
        <strong className="font-semibold text-text">{MAIL_ABSENDER.name}</strong>
        <span className="whitespace-nowrap">{MAIL_ABSENDER.adresse}</span>
      </p>

      <div className="mt-5 border-t border-linie-zart pt-4">
        {meldung && <Hinweis art={meldung.art}>{meldung.text}</Hinweis>}
        <p className="mt-0 mb-2.5 text-[0.875rem] leading-relaxed text-text-leise">
          {t('Nichts da? Sieh im Spam nach oder lass sie dir noch einmal schicken.', 'Nothing there? Check your spam folder or have it sent again.')}
        </p>
        {pruefung.feld}
        <button
          type="button" onClick={() => { setMeldung(null); pruefung.ausfuehren(nochmal); }} disabled={sendet || pruefung.wartet || sperre > 0}
          className="h-[2.75rem] w-full cursor-pointer rounded-lg border border-linie bg-flaeche px-4 text-[0.90625rem] font-medium text-text
                     hover:border-marke hover:text-marke-tief disabled:cursor-not-allowed disabled:text-text-hell disabled:hover:border-linie"
        >
          {sendet || pruefung.wartet ? t('Wird verschickt …', 'Sending …') : sperre > 0 ? <>{t('Mail erneut senden', 'Send email again')} <span className="tabular-nums">· {sperre} s</span></> : t('Mail erneut senden', 'Send email again')}
        </button>
        {andereAdresse && (
          <p className="mt-3 mb-0 text-sm text-text-hell">
            {t('Adresse vertippt?', 'Typo in the address?')}{' '}
            <button type="button" onClick={andereAdresse} className="cursor-pointer border-0 bg-transparent p-0 text-sm text-marke-tief underline decoration-marke/40 underline-offset-[3px] hover:decoration-marke">{t('Andere Adresse eingeben', 'Enter a different address')}</button>
          </p>
        )}
      </div>

      <p className="mt-5 mb-0 text-[0.78125rem] leading-relaxed text-text-hell">
        {t(
          <>Dein Startguthaben wird gebucht, sobald du bestätigt hast. Link schon geöffnet? <Verweis to="/login">Melde dich an</Verweis>.</>,
          <>Your starting credits are added as soon as you confirm. Already opened the link? <Verweis to="/login">Sign in</Verweis>.</>,
        )}
      </p>
    </Rahmen>
  );
}
